const { resolveName } = require('./resolveName');
const { updateHydration, addMessage, acknowledgeNewMessage } = require('../slices/messageSlice'); // Adjust path as needed


/** Extracts Binary resource URLs from a DocumentReference. */
export function getBinaryResourcesFromDocumentReference(documentRef) {
    if (!documentRef || !Array.isArray(documentRef.content)) return [];
    return documentRef.content
        .map(item => item.attachment?.url)
        .filter(url => typeof url === 'string' && url.includes('Binary/'));
}

/** Wraps a FHIR resource into a displayable message object. */
export function createWrappedMessage({
    sourceType,
    fileType,
    resourceId,
    title,
    resource,
    attachment,
    index
}) {
    return {
        type: sourceType,
        fileType,
        resourceId,
        title,
        date: resource?.date || null,
        sourceDescription: resource?.description || null,
        index,
        ...(sourceType === "DocumentReference" || sourceType === "Media"
            ? { resource }
            : { attachment }),
    };
}

/** Builds a Communication payload for sending. */
export function buildCommunicationPayload({
    contextProvider,
    recipients,
    selectedPatient,
    encounter,
    topic,
    priority,
    category,
    about,
    reason,
    anchorReference,
    replyToCommId,
    message,
    selectedDocuments
}) {
    const senderRef = `Practitioner/${contextProvider?.id}`;
    const now = new Date().toISOString();

    const payload = {
        resourceType: "Communication",
        status: "completed",
        sender: { reference: senderRef },
        recipient: recipients.map(r => ({
            reference: `${r.resourceType}/${r.id}`
        })),
        subject: { reference: `Patient/${selectedPatient?.id}` },
        encounter: { reference: `Encounter/${encounter?.id}` },
        topic: {
            coding: [{
                system: "http://example.com/custom-topic-system",
                code: "progress-update",
                display: topic
            }]
        },
        priority: priority || "routine",
        category: [{
            coding: [{
                system: "http://terminology.hl7.org/CodeSystem/communication-category",
                code: category,
                display: category.charAt(0).toUpperCase() + category.slice(1)
            }]
        }],
        about: about ? [{ reference: about }] : [],
        reasonCode: reason ? [{
            coding: [{
                system: "http://snomed.info/sct",
                code: reason,
                display: reason
            }]
        }] : [],
        ...(anchorReference && { partOf: [{ reference: anchorReference }] }),
        ...(replyToCommId && { inResponseTo: [{ reference: `Communication/${replyToCommId}` }] }),
        payload: [
            { contentString: message },
            ...selectedDocuments.map(doc => ({
                contentAttachment: {
                    contentType: doc.resource.contentType,
                    url: `Binary/${doc.resource.id}`,
                    title: doc.title || doc.resource.id
                }
            }))
        ],
        sent: now,
        extension: []
    };

    // ✅ Use isSelfMessage to determine if sender is messaging themselves
    const mockMessage = { sender: { reference: senderRef } };
    const isSelf = isSelfMessage(mockMessage, contextProvider?.id);

    if (!isSelf) {
        payload.extension.push({
            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
            extension: [
                {
                    url: "user",
                    valueReference: { reference: senderRef }
                },
                {
                    url: "readAt",
                    valueDateTime: now
                }
            ]
        });
    }

    return payload;
}

export const hydrateMessage = async ({
    msg,
    selfId,
    store,
    fhirClient
}) => {
    if (msg.meta?.hydrated) {
        console.log("🔁 Already hydrated:", msg.id);
        return msg;
    }

    console.log("💧 Hydrating message:", msg.id);

    const senderRef = msg.sender?.reference;
    const senderId = senderRef?.split("/")?.pop();
    const isSelf = senderId === selfId;
    const senderName = await resolveName(senderRef, store, fhirClient);
    const content = msg.payload?.find(p => p.contentString)?.contentString || "[No content]";
    const attachments = msg.payload?.filter(p => p.contentAttachment)?.map(p => p.contentAttachment) || [];

    const readReceipts = msg.extension?.filter(ext =>
        ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
    ) || [];

    const readDetails = await Promise.all(
        readReceipts
            .filter(ext => {
                const readerRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readerId = readerRef?.split("/")?.pop();
                return readerId !== senderId;
            })
            .map(async ext => {
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readerName = await resolveName(userRef, store, fhirClient);
                const readAt = ext.extension?.find(e => e.url === "readAt")?.valueDateTime;
                return readerName && readAt ? { reader: readerName, readAt } : null;
            })
    );

    const status = isSelf ? (msg.meta?.isPending ? "⏳ ...Sending" : "✅ Sent") : null;

    const enriched = {
        id: msg.id,
        sent: msg.sent,
        sender: senderName || senderRef || "Unknown",
        isSelf,
        content,
        attachments,
        status,
        readDetails: readDetails.filter(Boolean)
    };

    return {
        ...msg,
        meta: {
            ...(msg.meta || {}),
            hydrated: true,
            enrichedData: enriched,
            hydratedAt: new Date().toISOString()
        }
    };
};


//TODO: use this function instead of hydrateMessages in fetchNewMessages.js and see what happens.
/**
 * Hydrates and dispatches an array of Communication messages directly without searching for redux messages.
 *
 * @param {object[]} messages - Array of raw Communication resources
 * @param {object} contextProvider - Practitioner context
 * @param {object} store - Redux store
 * @param {object} fhirClient - FHIR client instance
 * @param {boolean} isMounted - Component mount flag
 * @param {function} dispatch - Redux dispatch
 * @returns {Promise<object[]>} - Array of hydrated messages
 */
export const hydrateMessageArray = async ({
    messages,
    contextProvider,
    store,
    fhirClient,
    isMounted,
    dispatch
}) => {
    try {
        if (!Array.isArray(messages) || messages.length === 0) {
            console.log("⚠️ No messages provided for hydration");
            return [];
        }

        console.log(`💧 Hydrating ${messages.length} messages from array...`);
        const selfId = contextProvider?.id?.split("/")?.pop();
        const hydratedMessages = [];

        for (const msg of messages) {
            if (!isMounted) {
                console.log("⛔ Aborting hydration — component unmounted.");
                break;
            }

            const hydrated = await hydrateMessage({
                msg,
                selfId,
                store,
                fhirClient
            });

            if (hydrated) {
                hydratedMessages.push(hydrated);
                dispatch(updateHydration({
                    id: hydrated.id,
                    enriched: hydrated.meta.enrichedData
                }));
            }
        }

        return hydratedMessages;
    } catch (err) {
        console.error("🚨 Error hydrating message array:", err);
        throw err;
    }
};


export const hydrateMessages = async ({
    messageIds,
    byId,
    contextProvider,
    store,
    fhirClient,
    isMounted,
    dispatch
}) => {
    try {
        console.log("💧 Starting individual hydration for messages:", messageIds);

        const selfId = contextProvider?.id?.split("/")?.pop();
        const rawMessages = messageIds.map(id => byId?.[id]).filter(Boolean);
        const hydratedMessages = [];

        console.log(`💧 Hydrating ${rawMessages.length} messages individually...`);

        for (const msg of rawMessages) {
            console.log(`💧 Hydrating message ${msg.id}...`);
            if (!isMounted) {
                console.log("⛔ Aborting hydration — component unmounted.");
                break;
            }

            const hydrated = await hydrateMessage({
                msg,
                selfId,
                store,
                fhirClient
            });

            console.log(`💧 Hydrated message ${msg.id}:`, hydrated);

            if (hydrated) {
                hydratedMessages.push(hydrated);
                dispatch(updateHydration({
                    id: hydrated.id,
                    enriched: hydrated.meta.enrichedData
                }));
            }
        }

        return hydratedMessages;
    } catch (err) {
        console.error("🚨 Error hydrating messages individually:", err);
        throw err;
    }
};

// messageUtils.js

/**
 * Checks if a Communication message is hydrated in Redux.
 * @param {Object} message - The Communication object (must have an `id`).
 * @param {Object} byId - The Redux slice containing messages keyed by ID.
 * @returns {boolean} - True if hydrated, false otherwise.
 */
export function isHydrated(message, byId) {
    if (!message?.id || !byId?.[message.id]) return false;
    return !!byId[message.id]?.meta?.hydrated;
};


export function hasBeenReadByCurrentUser(comm, userId) {
    if (!comm || !userId) return false;

    const normalizedUserId = userId.split("/")?.pop(); // e.g., "1234" from "Practitioner/1234"

    // ✅ If the sender is the current user, it's already read
    const senderRef = comm.sender?.reference;
    const normalizedSenderId = senderRef?.split("/")?.pop();
    if (normalizedSenderId === normalizedUserId) return true;

    // ✅ Otherwise, check for read receipts
    const receipts = comm.extension?.filter(
        (ext) => ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
    ) || [];

    return receipts.some((receipt) => {
        const userRef = receipt.extension?.find(e => e.url === "user")?.valueReference?.reference;
        const readAt = receipt.extension?.find(e => e.url === "readAt")?.valueDateTime;
        const normalizedRefId = userRef?.split("/")?.pop();

        return normalizedUserId === normalizedRefId && !!readAt;
    });
}



export function promoteMessageFromNewToMain(message, dispatch) {
    dispatch(addMessage(message)); // adds to messages.byId
    dispatch(acknowledgeNewMessage(message.id)); // removes from newMessages
};

//for updating read receipts on the right reducer.
export function dispatchMessageUpdate(message, dispatch, getState) {
    const state = getState();
    const inNewMessages = !!state.messages.newMessages.byId[message.id];

    if (inNewMessages) {
        dispatch(acknowledgeNewMessage(message.id));
    }

    dispatch(addMessage(message));
};

export function isSelfMessage(message, providerId) {
    if (!message || !message.sender || !providerId) return false;

    // FHIR references usually look like "Practitioner/123"
    const senderRef = message.sender.reference || "";
    return senderRef === `Practitioner/${providerId}`;
};

/**
 * Extracts attachments from a FHIR Communication resource.
 * @param {Object} message - The FHIR Communication resource.
 * @returns {Array} - Array of attachment objects.
 */
export function getMessageAttachments(message) {
    if (!message || !Array.isArray(message.payload)) return [];
    return message.payload.filter((entry) => entry.contentAttachment).map((entry) => entry.contentAttachment);
};

/**
 * Builds a FHIR AuditEvent agent object from a contextProvider.
 * @param {Object} contextProvider - The Practitioner resource or null.
 * @returns {Object} FHIR AuditEvent agent object.
 */
export function buildAgentFromContextProvider(contextProvider) {
    if (contextProvider) {
        return {
            who: {
                reference: `Practitioner/${contextProvider.id}`,
                display:
                    contextProvider.name?.[0]?.text ||
                    `${contextProvider.name?.[0]?.given?.join(" ")} ${contextProvider.name?.[0]?.family}` ||
                    "Unknown"
            },
            requestor: true
        };
    }
    return {
        who: { reference: "Practitioner/unknown", display: "Unknown" },
        requestor: true
    };
}

/**
 * Builds a FHIR AuditEvent entity object from a contextPatient.
 * @param {Object} contextPatient - The Patient resource or null.
 * @returns {Object} FHIR AuditEvent entity object.
 */
export function buildEntityFromContextPatient(contextPatient) {
    if (contextPatient) {
        return {
            what: {
                reference: `Patient/${contextPatient.id}`,
                display:
                    contextPatient.name?.[0]?.text ||
                    `${contextPatient.name?.[0]?.given?.join(" ")} ${contextPatient.name?.[0]?.family}` ||
                    "Unknown"
            },
            role: { code: "1", display: "Patient" }
        };
    }
    return {
        what: { reference: "Patient/unknown", display: "Unknown" },
        role: { code: "1", display: "Patient" }
    };
}
